import React, { useState } from 'react'
import { Box, Alert, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, Chip } from '@mui/material'
import { MultiTableConfigTable } from '../../../../types/gotypes'
import { TableSourcePicker } from './TableSourcePicker'
import { ConfiguredSourceRef } from './wizardHelpers'

/**
 * What the user decided for one broken reference (#1239). Staged locally
 * until "Apply and regenerate"; nothing is written per row.
 */
export type ReferenceDecision =
  | { ref: ConfiguredSourceRef; action: 'remove' }
  | { ref: ConfiguredSourceRef; action: 'replace'; replacement: MultiTableConfigTable }

export interface MissingReferencesBannerProps {
  customer: string
  job: string
  missing: ConfiguredSourceRef[]
  /**
   * Called ONCE with every decision — the card saves the new table list in
   * one write and regenerates once (#1239). Never called with fewer
   * decisions than missing references.
   */
  onApply: (decisions: ReferenceDecision[]) => void
  /** True while the card is saving/regenerating after Apply. */
  applying?: boolean
  /**
   * #1219 gate 2: false when the viewer may not overwrite this dashboard.
   * Apply would be refused server-side, so the banner still names every
   * missing reference but offers no decision UI — Duplicate is the way to
   * fix it on a copy. Defaults true so every other caller is unaffected.
   */
  canApply?: boolean
}

/**
 * Replaces the report content when one or more configured table references
 * no longer resolve — renamed, deleted, moved (#970 Part C). Every missing
 * reference is listed. Since #1239 each row takes a DECISION (Replace… or
 * Remove) that is only staged here; one "Apply and regenerate" commits them
 * all — one config write, one analysis — instead of N re-runs for N rows.
 */
export const MissingReferencesBanner: React.FC<MissingReferencesBannerProps> = ({
  customer,
  job,
  missing,
  onApply,
  applying = false,
  canApply = true,
}) => {
  const [decisions, setDecisions] = useState<Record<string, ReferenceDecision>>({})
  const [replacingRef, setReplacingRef] = useState<ConfiguredSourceRef | null>(null)
  const [pickerSelection, setPickerSelection] = useState<MultiTableConfigTable[]>([])

  const decide = (d: ReferenceDecision) => setDecisions(prev => ({ ...prev, [d.ref.fullPath]: d }))
  const undo = (ref: ConfiguredSourceRef) => setDecisions(prev => {
    const next = { ...prev }
    delete next[ref.fullPath]
    return next
  })

  const handleConfirmReplacement = () => {
    if (!replacingRef || pickerSelection.length === 0) return
    decide({ ref: replacingRef, action: 'replace', replacement: pickerSelection[0] })
    setReplacingRef(null)
    setPickerSelection([])
  }

  const handleRemoveAll = () => {
    const next: Record<string, ReferenceDecision> = {}
    for (const ref of missing) next[ref.fullPath] = { ref, action: 'remove' }
    setDecisions(next)
  }

  const decided = missing.filter(ref => decisions[ref.fullPath])
  const allDecided = decided.length === missing.length && missing.length > 0

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {missing.map(ref => {
        const decision = decisions[ref.fullPath]
        return (
          <Alert key={ref.fullPath} severity="warning" variant="outlined">
            <Typography variant="body2" fontWeight={600}>
              [{ref.displayName}] is no longer available.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Previously stored at: {ref.fullPath}
            </Typography>
            {canApply && (decision ? (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  color={decision.action === 'remove' ? 'default' : 'info'}
                  label={decision.action === 'remove' ? 'Removed' : `→ ${decision.replacement.displayName}`}
                  data-testid="reference-decision"
                />
                <Button size="small" variant="text" onClick={() => undo(ref)} disabled={applying}>
                  Undo
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" variant="outlined" color="info" disabled={applying}
                  onClick={() => { setReplacingRef(ref); setPickerSelection([]) }}>
                  Replace…
                </Button>
                <Button size="small" variant="text" color="error" disabled={applying}
                  onClick={() => decide({ ref, action: 'remove' })}>
                  Remove
                </Button>
              </Box>
            ))}
          </Alert>
        )
      })}

      {/* Banner-level actions: decide everything, then commit once — hidden
          entirely when the viewer can't overwrite this dashboard; Duplicate
          is their route instead (#1219 gate 2). */}
      {canApply ? (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button size="small" variant="text" color="error" onClick={handleRemoveAll} disabled={applying}>
          Remove all references
        </Button>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {decided.length} of {missing.length} decided
        </Typography>
        <Button size="small" variant="text" onClick={() => setDecisions({})} disabled={applying || decided.length === 0}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          color="info"
          disabled={!allDecided || applying}
          onClick={() => onApply(missing.map(ref => decisions[ref.fullPath]))}
        >
          {applying ? 'Applying…' : 'Apply and regenerate'}
        </Button>
      </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Duplicate this dashboard to fix the references on your own copy.
        </Typography>
      )}

      <Dialog open={Boolean(replacingRef)} onClose={() => setReplacingRef(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Select a replacement for {replacingRef?.displayName}</DialogTitle>
        <DialogContent>
          <TableSourcePicker
            customer={customer}
            job={replacingRef?.job || job}
            selected={pickerSelection}
            onSelectedChange={setPickerSelection}
            singleSelect
            height={320}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplacingRef(null)}>Cancel</Button>
          <Button variant="contained" color="info" disabled={pickerSelection.length === 0} onClick={handleConfirmReplacement}>
            Use this table
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default MissingReferencesBanner
